import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useOptionsApi, useProfileStore } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { useLessonRecurringStore } from './recurring/store'
import { useLessonSlotsApi } from '../../api/lessons/slots.hook'
import { createRecurringDays, getWeekdayOfDate } from './lesson-recurring'

interface LessonRecurringPageReturn {
  refIsLoading: ComputedRef<boolean>
}

/** Opens the recurring form on today's weekday, with no day picked, in the admin view timezone. The page loader also
 *  covers a schedule-only submit; the tutor modal shows its own loader for its read and submit. */
export const useLessonRecurringPage = (): LessonRecurringPageReturn => {
  const route = useRoute()
  const dayjs = useDayjs()
  const store = useLessonRecurringStore()
  const profileStore = useProfileStore()

  const refIsLoading = computed<boolean>(() => store.isLoading || (store.isSubmitting && !store.getModal))

  const getToday = (): string => {
    const now = profileStore.getCurrentTime

    return now
      ? dayjs(now, DateFormat.Default).format(DateFormat.DefaultDate)
      : dayjs().tz(profileStore.getTimezone).format(DateFormat.DefaultDate)
  }

  const init = async (): Promise<void> => {
    store.reset()
    store.setLoading(true)

    // The server clock decides "today"; the one read at sign-in may be hours old.
    const [slots] = await Promise.all([
      useLessonSlotsApi({ disciplineId: route.params.id as string }),
      useOptionsApi(),
    ])

    const today = getToday()

    store.setSlots(slots)
    store.setToday(today)
    store.setStartDate(today)
    store.setFinishDate(today)
    store.setDays(createRecurringDays(slots?.duration[0] ?? 0))
    store.setSelectedDay(getWeekdayOfDate(today))
    store.setLoading(false)
  }

  onMounted(init)

  onBeforeUnmount(() => {
    store.reset()
  })

  return {
    refIsLoading,
  }
}
